<template>
    <div class="skill-action-bar" :class="roundRole ? 'skill-action-bar--' + roundRole : ''">
        <div v-if="roundRole" class="skill-action-bar__round-banner btn-shadow-static">
            <v-icon size="16" class="mr-1" color="black">{{ roundRole === 'attack' ? 'mdi-sword-cross' : 'mdi-shield-half-full' }}</v-icon>
            {{ $t('journey.skillActionBar.nextRoundPrefix') }} {{ roundRole === 'attack' ? $t('journey.skillActionBar.yourAttack') : $t('journey.skillActionBar.yourDefense') }}
            <span v-if="nextRoundHint" class="skill-action-bar__round-banner-sub">{{ nextRoundHint }}</span>
        </div>
        <div
            class="skill-action-bar__trigger"
            :class="{'skill-action-bar__trigger--selected': !!selectedSkill, 'skill-action-bar__trigger--disabled': !canFight, 'skill-action-bar__trigger--dark': $vuetify.theme.dark}"
            @click="canFight && $emit('open-picker')"
        >
            <template v-if="selectedSkill">
                <v-icon size="18" class="mr-1">{{ selectedSkill.defence > 0 ? (selectedSkill.defence > 1 ? 'mdi-shield-half-full' : 'mdi-laser-pointer') : 'mdi-sword-cross' }}</v-icon>
                <span class="skill-action-bar__name">{{ selectedSkill.name }}</span>
                <span class="skill-action-bar__dmg">{{ selectedSkill.minDmg }}-{{ selectedSkill.maxDmg }}</span>
                <v-icon v-if="selectedSkill.spread" size="16" class="mr-1">mdi-earth</v-icon>
                <template v-if="effectIcon(selectedSkill)">
                    <v-icon size="16" class="mr-1">{{ effectIcon(selectedSkill) }}</v-icon>
                    <span class="skill-action-bar__effect">{{ effectValue(selectedSkill) }}</span>
                </template>
                <span v-else-if="selectedSkill.effect" class="skill-action-bar__effect">{{ getEffectFormula(selectedSkill) }}</span>
                <TypePill :type="selectedSkill.type" size="x-small" class="mr-1">{{ selectedSkill.type.id }}</TypePill>
                <v-spacer />
                <v-icon size="18" @click.stop="$emit('clear-skill')">mdi-close</v-icon>
            </template>
            <template v-else>
                <v-icon size="18" class="mr-1">mdi-sword-cross</v-icon>
                <span class="skill-action-bar__name">{{ baseLabel }}</span>
                <span v-if="allowBaseMove" class="skill-action-bar__dmg">1-6</span>
                <v-spacer />
                <v-icon size="18">mdi-menu-down</v-icon>
            </template>
        </div>
        <div class="skill-action-bar__buttons">
            <v-btn
                :loading="isLoading"
                :disabled="!canFight || !hasUsableSkills || (requireSelection && !selectedSkill)"
                :color="useSkillColor + ' black--text btn-shadow'"
                @click="$emit('use-skill')"
            >
                {{ useSkillLabel }}
            </v-btn>
            <v-btn
                v-if="allowBaseMove"
                :loading="isLoading"
                :disabled="!canFight"
                class="btn-shadow skill-action-bar__base-move-btn"
                :class="{'skill-action-bar__base-move-btn--dark': $vuetify.theme.dark}"
                @click="$emit('use-base-move')"
            >
                {{ baseLabel }}
            </v-btn>
        </div>
    </div>
</template>

<script>
// Výběr skillu + jeho použití - vlastní komponenta nad SkillPickerDialog (viz BattleScreen.vue).
// Umístění (fixed nad action barem na mobilu) řeší BattleLayout.vue - tahle komponenta jen kreslí
// svůj obsah. Design/rozhodnutí viz claude/components/battle-skills.md.
import TypePill from '@/components/utils/TypePill';
import {getEffectFormula, effectIcon, effectValue} from '@/utils/skillDisplay';
import {mapGetters} from 'vuex';

export default {
    name: 'SkillActionBar',
    components: {TypePill},
    props: {
        skills: {type: Array, default: () => []},
        selectedSkillId: {type: Number, default: null},
        canFight: {type: Boolean, default: false},
        // Obstacle mód nemá "base move" ekvivalent - backend vyžaduje vždy reálný skillId.
        baseLabel: {type: String, required: true},
        allowBaseMove: {type: Boolean, default: true},
        requireSelection: {type: Boolean, default: false},
        // 'attack'/'defence'/null - null zachovává generický label (obstacle mód).
        roundRole: {type: String, default: null},
        // Vysvětlující řádek pod NEXT ROUND banerem - hotový text nebo null (počítá BattleScreen.vue).
        nextRoundHint: {type: String, default: null},
    },
    computed: {
        ...mapGetters({
            isLoading: 'journey/isLoading',
        }),
        useSkillLabel() {
            if (this.roundRole === 'attack') return this.$t('journey.skillActionBar.attackSkillLabel');
            if (this.roundRole === 'defence') return this.$t('journey.skillActionBar.defenceSkillLabel');
            return this.$t('journey.skillActionBar.useSkillLabel');
        },
        useSkillColor() {
            return this.roundRole === 'defence' ? 'themeJenny' : 'error';
        },
        selectedSkill() {
            if (!this.selectedSkillId) {
                return null;
            }
            return this.skills.find((s) => s.id === this.selectedSkillId) || null;
        },
        hasUsableSkills() {
            return this.skills.some((s) => s.disabled === false);
        },
    },
    methods: {
        getEffectFormula,
        effectIcon,
        effectValue,
    },
}
</script>

<style scoped>
.skill-action-bar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 12px;
}
.skill-action-bar__round-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    text-align: center;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.03em;
    color: #000;
    line-height: 1.3;
}
/* error/themeJenny jsou registrované Vuetify theme barvy (vuetify.js); tmavší gradient stop je
   ručně dopočítaný hex fallback (CSS gradient neumí automaticky ztmavit CSS proměnnou). */
.skill-action-bar--attack .skill-action-bar__round-banner {
    background: linear-gradient(90deg, #A9243D, var(--v-error-base, #EF5167));
}
.skill-action-bar--defence .skill-action-bar__round-banner {
    background: linear-gradient(90deg, #125A94, var(--v-themeJenny-base, #1E88E5));
}
.skill-action-bar__round-banner-sub {
    flex-basis: 100%;
    font-size: 9px;
    font-weight: 500;
    opacity: 0.85;
    margin-top: 1px;
    white-space: normal;
    overflow-wrap: break-word;
}
.skill-action-bar__trigger {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.3);
    border: 1px solid rgba(255,255,255,0.35);
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    color: rgba(0,0,0,0.85);
    font-size: 14px;
}
.skill-action-bar__trigger--selected {
    color: #000;
    font-weight: 600;
}
.skill-action-bar__trigger--dark {
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,255,255,0.35);
    color: rgba(255,255,255,0.85);
}
.skill-action-bar__trigger--dark.skill-action-bar__trigger--selected {
    color: #fff;
}
.skill-action-bar--attack .skill-action-bar__trigger {
    border-color: var(--v-error-base, #EF5167);
}
.skill-action-bar--defence .skill-action-bar__trigger {
    border-color: var(--v-themeJenny-base, #1E88E5);
}
.skill-action-bar__trigger--disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
}
.skill-action-bar__name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;
}
.skill-action-bar__dmg {
    font-size: 13px;
    opacity: 0.85;
    margin-right: 8px;
    white-space: nowrap;
}
.skill-action-bar__effect {
    font-size: 12px;
    opacity: 0.85;
    margin-right: 8px;
    white-space: nowrap;
}
.skill-action-bar__buttons {
    display: flex;
    justify-content: center;
    gap: 12px;
}
.skill-action-bar__base-move-btn {
    background-color: #ffffff !important;
    color: #000 !important;
}
.skill-action-bar__base-move-btn--dark {
    background-color: #646464 !important;
    color: #fff !important;
}
</style>
